import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { resetSupabase } from '../lib/supabaseResetClient';
import { t } from '../i18n/nl.js';

// Publieke route, buiten <ProtectedRoute> (zelfde patroon als
// /account-verwijderen en /overeenkomst/:token in App.jsx). Vangt de
// Supabase recovery-link op die vanuit de app-flow (useAuth.resetPassword)
// wordt verstuurd, nu met redirectTo naar deze pagina i.p.v. het mobiele
// deep-link-scheme.
//
// Supabase stuurt het recovery-token als hash-fragment
// (#access_token=...&type=recovery), niet als query-parameter. Deze pagina
// gebruikt daarvoor een EIGEN, niet-persistente Supabase-client
// (../lib/supabaseResetClient.js) — nooit de gedeelde, persistente client
// uit ../lib/supabase.js, die dit hash-fragment bewust NIET meer verwerkt
// (detectSessionInUrl: false, zie aldaar). Zo kan een recovery-sessie het
// sluiten van het tabblad nooit overleven, en kan een sessie op déze
// client nooit van iets anders dan déze recovery-link afkomstig zijn.
//
// De client vuurt een 'PASSWORD_RECOVERY'-auth-event zodra het
// hash-fragment succesvol tot een sessie leidt. Omdat er een race bestaat
// tussen client-initialisatie en het aanmelden van de listener hieronder
// (de initialisatie kan al voltooid zijn vóór de listener actief is),
// wordt daarnaast vertrouwd op 'INITIAL_SESSION' — dat event wordt door
// supabase-js altijd (opnieuw) uitgezonden zodra een nieuwe listener zich
// aanmeldt, ná afronding van de eigen initialisatie, en toont dus
// betrouwbaar de resulterende sessiestatus. Omdat dit een toegewijde,
// niet-persistente client is die nergens anders wordt gebruikt (geen
// login/signIn-aanroepen elders), kan een sessie die hier verschijnt nooit
// een "toevallige" sessie van een ander account zijn — alleen déze
// recovery-link kan hem hebben aangemaakt.
//
// Een verlopen/al-gebruikte link geeft i.p.v. een token een foutfragment
// (#error=access_denied&error_code=otp_expired&...).
export default function ResetWachtwoordPage({ activationMode = false }) {
  const [status, setStatus] = useState('laden'); // laden | klaar | ongeldig | succes
  const [nieuwWachtwoord, setNieuwWachtwoord] = useState('');
  const [herhaalWachtwoord, setHerhaalWachtwoord] = useState('');
  const [error, setError] = useState('');
  const [opslaan, setOpslaan] = useState(false);
  const navigate = useNavigate();
  // Blijft `false` totdat het wachtwoord succesvol is opgeslagen; gebruikt
  // door de unmount-cleanup hieronder om te bepalen of een nooit-afgeronde
  // recovery-sessie moet worden opgeruimd (signOut) als de gebruiker
  // wegnavigeert vóór afronding. Dekt niet het sluiten van tabblad/browser
  // zonder navigatie — zie BRIEFING.md voor dat restrisico.
  const succesBereiktRef = useRef(false);

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    if (hashParams.get('error')) {
      setStatus('ongeldig');
      return undefined;
    }

    let afgehandeld = false;

    const { data: { subscription } } = resetSupabase.auth.onAuthStateChange((event, session) => {
      if (afgehandeld) return;
      if (event === 'PASSWORD_RECOVERY') {
        afgehandeld = true;
        setStatus('klaar');
      } else if (event === 'INITIAL_SESSION') {
        // Vangnet voor de race hierboven beschreven: dit event komt altijd,
        // en toont de sessie die (uitsluitend) uit déze recovery-link kan
        // voortkomen op deze toegewijde client.
        afgehandeld = true;
        setStatus(session ? 'klaar' : 'ongeldig');
      }
    });

    // Uiterste vangnet: zou geen van beide events binnen deze tijd komen
    // (bv. een gehangen netwerkverzoek), toon dan alsnog "verlopen" i.p.v.
    // oneindig te laten laden. INITIAL_SESSION wacht zelf al op de volledige
    // (netwerk-)initialisatie, dus dit hoeft normaliter nooit te vuren.
    const timeout = setTimeout(() => {
      if (!afgehandeld) setStatus('ongeldig');
    }, 8000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
      // Sessie opruimen als de gebruiker wegnavigeert terwijl er wél een
      // recovery-sessie actief was, maar het wachtwoord nooit succesvol is
      // gewijzigd (die geslaagde flow ruimt zichzelf al op, zie handleSubmit).
      if (afgehandeld && !succesBereiktRef.current) {
        resetSupabase.auth.signOut().catch(() => {});
      }
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!nieuwWachtwoord || nieuwWachtwoord.length < 6) {
      setError(t('wachtwoordReset.foutTeKort'));
      return;
    }
    if (nieuwWachtwoord !== herhaalWachtwoord) {
      setError(t('wachtwoordReset.foutMismatch'));
      return;
    }
    setOpslaan(true);
    try {
      const { error: err } = await resetSupabase.auth.updateUser({ password: nieuwWachtwoord });
      if (err) throw err;
      if (activationMode) {
        const { error: activationError } = await resetSupabase.rpc('mark_account_activated');
        if (activationError) throw activationError;
      }
      // Wachtwoord is vanaf hier al gewijzigd — een falende signOut hierna
      // mag de gebruiker niet alsnog een foutmelding tonen (regressie: zie
      // onderdeel 3 van de beveiligingsanalyse).
      succesBereiktRef.current = true;
      setStatus('succes');
      // supabase-js gooit hier normaliter niet, maar geeft { error } terug —
      // zowel dat geval als een onverwachte throw worden hier afgevangen
      // zonder de al-getoonde succesmelding te beïnvloeden.
      try {
        const { error: signOutErr } = await resetSupabase.auth.signOut();
        if (signOutErr) throw signOutErr;
      } catch (signOutErr) {
        console.error('signOut na wachtwoordwijziging mislukt:', signOutErr);
        Sentry.captureException(signOutErr);
      }
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err) {
      // Technische (Engelstalige) backend-fout niet rechtstreeks tonen —
      // wel loggen naar Sentry, gebruiker krijgt een vaste NL-melding. Eén
      // bekende foutcode ('same_password') krijgt een passender eigen tekst.
      console.error('wachtwoord-reset mislukt:', err);
      Sentry.captureException(err);
      setError(err?.code === 'same_password'
        ? t('wachtwoordReset.foutZelfdeWachtwoord')
        : t('wachtwoordReset.foutOnbekend'));
    } finally {
      setOpslaan(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--color-bg)',
      padding: 20,
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 52,
            height: 52,
            background: 'var(--color-primary)',
            borderRadius: 14,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            marginBottom: 16,
          }}>
            💪
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-text)' }}>
            {t('app.name')}
          </h1>
        </div>

        <div className="card">
          {status === 'laden' && (
            <p style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
              <span className="spinner" style={{ width: 16, height: 16, marginRight: 8 }} />
              {t('wachtwoordReset.laden')}
            </p>
          )}

          {status === 'klaar' && (
            <form onSubmit={handleSubmit}>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--color-text)', marginBottom: 6 }}>
                {t(activationMode ? 'accountActivation.title' : 'wachtwoordReset.titel')}
              </h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: 14, marginBottom: 20 }}>
                {t(activationMode ? 'accountActivation.intro' : 'wachtwoordReset.intro')}
              </p>

              {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

              <div className="form-group">
                <label className="form-label" htmlFor="nieuwWachtwoord">{t('wachtwoordReset.labelNieuw')}</label>
                <input
                  id="nieuwWachtwoord"
                  type="password"
                  className="form-input"
                  value={nieuwWachtwoord}
                  onChange={e => setNieuwWachtwoord(e.target.value)}
                  placeholder={t('wachtwoordReset.placeholderNieuw')}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group" style={{ marginBottom: 24 }}>
                <label className="form-label" htmlFor="herhaalWachtwoord">{t('wachtwoordReset.labelHerhaal')}</label>
                <input
                  id="herhaalWachtwoord"
                  type="password"
                  className="form-input"
                  value={herhaalWachtwoord}
                  onChange={e => setHerhaalWachtwoord(e.target.value)}
                  placeholder={t('wachtwoordReset.placeholderNieuw')}
                  required
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '11px 16px', fontSize: 15 }}
                disabled={opslaan}
              >
                {opslaan ? <><span className="spinner" style={{ width: 16, height: 16 }} /> {t('auth.loading')}</> : t(activationMode ? 'accountActivation.submit' : 'wachtwoordReset.opslaanKnop')}
              </button>
            </form>
          )}

          {status === 'succes' && (
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--color-text)', marginBottom: 6 }}>
                {t(activationMode ? 'accountActivation.successTitle' : 'wachtwoordReset.succesTitel')}
              </h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: 14, marginBottom: 16 }}>
                {t(activationMode ? 'accountActivation.successText' : 'wachtwoordReset.succesUitleg')}
              </p>
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '11px 16px', fontSize: 15 }}
                onClick={() => navigate('/login', { replace: true })}
              >
                {t('wachtwoordReset.succesNaarInloggen')}
              </button>
            </div>
          )}

          {status === 'ongeldig' && (
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--color-text)', marginBottom: 6 }}>
                {t('wachtwoordReset.ongeldigTitel')}
              </h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: 14, marginBottom: 16 }}>
                {t('wachtwoordReset.ongeldigUitleg')}
              </p>
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '11px 16px', fontSize: 15 }}
                onClick={() => navigate('/login', { replace: true })}
              >
                {t('wachtwoordReset.naarInloggen')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
